// Navigation link addresses — one rule for the header and the editors.
//
// A navigation item (Navigation menu node, custom menu node, bar button)
// points at ONE of two things:
//
//   • a Stride page  — a path on this site (/structure, /dashboards/x,
//                      /customs/floor) — opens as an app tab;
//   • a web address  — anything else (https://…, mailto:, another Stride
//                      site, a sister Zebra app) — opens in a new browser
//                      tab, outside the shell.
//
// "This site" means the page's own origin and NOTHING else. Before this
// module the header treated every *.onrender.com and *.zebra-innovation.com
// address as its own (a carry-over from the archive's host moves), which
// hijacked links to other apps on those hosts into local paths that 404'd.
// Both faces of the seam are pure functions so a static suite can pin them.

// A scheme prefix (https:, mailto:, tel:) — but NOT a host with a port
// ("intranet:8080/wiki"), which is a web address missing its scheme.
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:(?!\d+(?:[/?#]|$))/i;

// Normalise what an admin typed into the shape the tree stores. Returns
// null for an empty address (a parent row that only groups children).
//   '/structure'           → '/structure'     (a Stride page, as typed)
//   'structure'            → '/structure'     (a bare page name)
//   'https://x.y/z'        → 'https://x.y/z'  (a web address, as typed)
//   'www.example.com/docs' → 'https://www.example.com/docs'
//   'mailto:ops@x.y'       → 'mailto:ops@x.y'
export function normaliseNavUrl(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith('/') || s.startsWith('#') || s.startsWith('?')) return s;
  if (SCHEME_RE.test(s)) return s;
  // No scheme, no leading slash: a host-looking value (a dot before any
  // slash, or a port) is a web address; a plain word is a page on this site.
  const head = s.split(/[/?#]/)[0];
  if (head.includes('.') || head.includes(':') || head.toLowerCase() === 'localhost') return `https://${s}`;
  return `/${s}`;
}

// The address the header navigates to: an absolute address on THIS origin
// collapses to its path (so a copied browser URL opens as an app tab);
// everything else stays as it is. `origin` is window.location.origin.
export function resolveNavUrl(raw, origin) {
  const url = normaliseNavUrl(raw);
  if (!url) return null;
  if (!/^https?:/i.test(url)) return url;
  try {
    const parsed = new URL(url);
    if (origin && parsed.origin === origin) return (parsed.pathname + parsed.search + parsed.hash) || '/';
  } catch { /* not parseable — hand it over as typed */ }
  return url;
}

// True when the (resolved) address leaves the app: anything with a scheme.
// A Stride page is a path; the shell opens it as a tab.
export function isExternalNavUrl(url) {
  if (!url) return false;
  return SCHEME_RE.test(String(url));
}
